import React, { useEffect, useId, useState } from 'react';
import { Button, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import EmploymentAttestationSummary from '@/components/hiring/EmploymentAttestationSummary';
import SharedAnswersSummary from '@/features/applications/student/blocks/SharedAnswersSummary';
import studentApplicationService from '@/shared/services/studentApplicationService';
import StatusPill from '@/components/common/StatusPill';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';
import { formatAnswerBy, formatAnswerDay } from '@/shared/utils/deadline';
import CandidateAnswers, { Paragraphs } from './CandidateAnswers';
import CandidateFeedback from './CandidateFeedback';
import CandidateKeyFacts from './CandidateKeyFacts';
import CandidateStageControls from './CandidateStageControls';
import CommitmentSummary from './CommitmentSummary';
import InterviewDetailsForm from './InterviewDetailsForm';
import RankSelect from './RankSelect';
import { EndReasonSummary } from './ReleaseReasonDialog';
import {
    OFFER_FROM,
    REVIEWABLE,
    StageBadge,
    formatDate,
    formatShortDate,
    formatShortDateTime,
    reviewerStageOf,
} from './reviewerStages';
import './hiring.css';

const headingTag = (level) => `h${Math.min(level, 6)}`;

function Section({ level, title, children }) {
    const Heading = headingTag(level);
    return (
        <section className="mt-3">
            <Heading className="h6">{title}</Heading>
            {children}
        </section>
    );
}

/**
 * What only the program's coordinators read: the 900 number and address, the
 * application's status, SI work-study, the pool and campus employment answers
 * and the student's employment confirmation with its international status.
 * Mounted in coordinator mode only, so faculty mode never requests the
 * student application; a Learning Assistant's faculty are sent their share of
 * these answers in the candidate (CandidateAnswers).
 */
function CoordinatorDetails({ candidacy, level }) {
    const { student, profile } = candidacy;
    const profileId = profile?.id;
    const [application, setApplication] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!profileId) return undefined;
        let active = true;
        setApplication(null);
        setFailed(false);
        studentApplicationService
            .getApplicationById(profileId)
            .then((answer) => {
                if (active) setApplication(answer);
            })
            .catch(() => {
                if (active) setFailed(true);
            });
        return () => {
            active = false;
        };
    }, [profileId]);

    const references = profile?.references || [];
    const workStudy = profile?.si;

    return (
        <Section level={level} title="Program records">
            <dl className="row mb-2">
                {student?.email && (
                    <>
                        <dt className="col-sm-4 fw-normal text-body-secondary">
                            Email
                        </dt>
                        <dd className="col-sm-8 mb-1">{student.email}</dd>
                    </>
                )}
                {student?.studentId && (
                    <>
                        <dt className="col-sm-4 fw-normal text-body-secondary">
                            900 number
                        </dt>
                        <dd className="col-sm-8 mb-1">{student.studentId}</dd>
                    </>
                )}
                {profile?.status && (
                    <>
                        <dt className="col-sm-4 fw-normal text-body-secondary">
                            Student application
                        </dt>
                        <dd className="col-sm-8 mb-1">
                            <StatusPill
                                family="studentApplication"
                                value={profile.status}
                            />
                        </dd>
                    </>
                )}
                {references.length > 0 && (
                    <>
                        <dt className="col-sm-4 fw-normal text-body-secondary">
                            References
                        </dt>
                        <dd className="col-sm-8 mb-1">
                            <ul className="list-unstyled mb-0">
                                {references.map((reference, index) => (
                                    <li key={index}>
                                        {[
                                            reference.name,
                                            reference.relationship,
                                            reference.email,
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                        {reference.formSubmitted
                                            ? ' (form submitted)'
                                            : ' (form not submitted)'}
                                    </li>
                                ))}
                            </ul>
                        </dd>
                    </>
                )}
                {workStudy && workStudy.workStudyEligible != null && (
                    <>
                        <dt className="col-sm-4 fw-normal text-body-secondary">
                            Work-study
                        </dt>
                        <dd className="col-sm-8 mb-1">
                            {workStudy.workStudyEligible
                                ? 'Eligible'
                                : 'Not eligible'}
                            {workStudy.workStudyEligible &&
                                workStudy.workStudyAmount != null &&
                                `, $${workStudy.workStudyAmount.toLocaleString('en-US')}`}
                        </dd>
                    </>
                )}
            </dl>
            {profileId && (
                <p className="mb-2">
                    <Link to={`/student-applications/${profileId}`}>
                        Open the full application
                        <span className="visually-hidden">
                            {` for ${student?.name || 'this student'}`}
                        </span>
                    </Link>
                </p>
            )}
            {failed && (
                <p className="mb-0">
                    The employment confirmation couldn&apos;t be loaded.
                </p>
            )}
            {application && (
                <>
                    <SharedAnswersSummary
                        application={{
                            generalPool: application.generalPool,
                            campusEmployment: application.campusEmployment,
                        }}
                    />
                    <EmploymentAttestationSummary
                        attestation={application.employmentAttestation}
                        unmet={application.employmentUnmet}
                    />
                </>
            )}
        </Section>
    );
}

function HistoryList({ history, mode }) {
    return (
        <ol className="list-unstyled mb-0">
            {history.map((entry, index) => (
                <li key={`${entry.at}-${index}`}>
                    {`${reviewerStageOf(entry.stage).label}, ${formatDate(entry.at)}`}
                    {mode === 'coordinator' &&
                        entry.role &&
                        `, by ${entry.role}`}
                    {mode === 'coordinator' && entry.note && (
                        <span className="d-block small text-body-secondary">
                            {`Note: ${entry.note}`}
                        </span>
                    )}
                </li>
            ))}
        </ol>
    );
}

const interviewText = (interview) =>
    [interview.when && formatShortDateTime(interview.when), interview.where]
        .filter(Boolean)
        .join(', ');

// The one line under the name that says where the candidate stands now.
function statusLine(candidacy, name) {
    const { stage, offer, interview } = candidacy;
    if (stage === 'offered' && offer) {
        return [
            `Offer made ${formatShortDate(offer.offeredAt)}.`,
            offer.respondBy && `Answer by ${formatAnswerBy(offer.respondBy)}.`,
        ]
            .filter(Boolean)
            .join(' ');
    }
    if (stage === 'expired' && offer?.respondBy) {
        return `The offer to ${name} expired on ${formatAnswerDay(offer.respondBy)} without an answer. The place is held until you choose what happens next.`;
    }
    if (interview && REVIEWABLE.includes(stage)) {
        const text = interviewText(interview);
        if (text) return `Interview: ${text}`;
    }
    if (candidacy.createdAt)
        return `Applied ${formatShortDate(candidacy.createdAt)}`;
    return '';
}

/**
 * One candidate on a position, as the detail beside the list (or its own
 * screen on a phone). A header that stays in view holds the name, the stage,
 * the ranking and the actions; below it the application (key facts first),
 * the interview when there is one, the notes, and the history. Faculty mode
 * renders an allow-list of fields, so it shows nothing a coordinator's
 * response carries beyond them. `onChange(candidacy, notice)` receives the
 * server's version after each write, and `onReload` reloads the pipeline
 * after a refusal that says the screen is stale.
 * `onReloadAndFocus(candidacyId, notice)` reloads it after a write that
 * changed another candidate too. `onPrevious`/`onNext` (with the names they
 * lead to, and `positionLabel`, where the candidate stands in the list) step through the list as filtered; `onBack` returns to the list
 * on a phone.
 */
export default function CandidatePanel({
    candidacy,
    position,
    access,
    mode,
    headingLevel = 3,
    siblings,
    rankCount,
    onChange,
    onReload,
    onReloadAndFocus,
    onPrevious,
    onNext,
    previousName,
    nextName,
    positionLabel,
    onBack,
    backLabel = 'Back to the list',
    headingRef,
}) {
    const baseId = useId();
    const locked = usePreviewLock();
    const [current, setCurrent] = useState(candidacy);
    useEffect(() => setCurrent(candidacy), [candidacy]);

    const coordinator = mode === 'coordinator';
    const Heading = headingTag(headingLevel);
    const level = headingLevel + 1;
    const { student, interview, offer } = current;
    const name = student?.name || 'A student';
    const canMoveStages = Boolean(access?.canMoveStages);
    const canOffer = Boolean(access?.canOffer);
    const reviewable = REVIEWABLE.includes(current.stage);
    const canRank = canMoveStages && reviewable;
    const headingId = `${baseId}-name`;
    const feedbackCount = (current.feedback || []).length;
    const showInterview = current.stage === 'interview' || Boolean(interview);
    const status = statusLine(current, name);

    const changed = (updated, notice) => {
        setCurrent(updated);
        onChange?.(updated, notice);
    };

    const courseLabel =
        [position?.subject, position?.courseNumber].filter(Boolean).join(' ') ||
        undefined;

    return (
        <article aria-labelledby={headingId} className="hiring-detail">
            <div className="hiring-detail-header bg-body border-bottom pb-3 mb-3">
                <div className="d-flex flex-wrap align-items-center gap-2">
                    {onBack && (
                        <Button
                            variant="link"
                            className="d-lg-none px-0 me-auto text-start"
                            onClick={onBack}
                        >
                            <span aria-hidden="true">&lsaquo; </span>
                            {backLabel}
                        </Button>
                    )}
                    <div className="hiring-detail-name d-flex flex-wrap align-items-center gap-2 order-last order-lg-first flex-grow-1">
                        <Heading
                            id={headingId}
                            ref={headingRef}
                            tabIndex={-1}
                            className="h4 mb-0"
                        >
                            {name}
                        </Heading>
                        <StageBadge stage={current.stage} />
                    </div>
                </div>
                {(onPrevious || onNext) && (
                    <nav
                        aria-label="Candidates in this list"
                        className="hiring-candidate-nav d-flex flex-wrap align-items-center gap-2 mt-2"
                    >
                        <Button
                            variant="outline-primary"
                            className="hiring-nav-button text-wrap"
                            disabled={!onPrevious}
                            onClick={onPrevious}
                            aria-label={
                                previousName
                                    ? `Previous candidate: ${previousName}`
                                    : 'Previous candidate'
                            }
                        >
                            <span aria-hidden="true">&lsaquo; </span>
                            Previous candidate
                        </Button>
                        {positionLabel && (
                            <span className="hiring-position-label small text-body-secondary text-nowrap">
                                {positionLabel}
                            </span>
                        )}
                        <Button
                            variant="outline-primary"
                            className="hiring-nav-button text-wrap"
                            disabled={!onNext}
                            onClick={onNext}
                            aria-label={
                                nextName
                                    ? `Next candidate: ${nextName}`
                                    : 'Next candidate'
                            }
                        >
                            Next candidate
                            <span aria-hidden="true"> &rsaquo;</span>
                        </Button>
                    </nav>
                )}
                <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1 mt-2">
                    {canRank ? (
                        <RankSelect
                            candidacy={current}
                            mode={mode}
                            count={rankCount}
                            disabled={locked}
                            onChange={changed}
                            onReload={onReload}
                        />
                    ) : (
                        current.positionRank != null && (
                            <span className="small">
                                {`${coordinator ? 'Ranking' : 'Your ranking'}: ${current.positionRank}`}
                            </span>
                        )
                    )}
                    {status && (
                        <span className="small text-body-secondary">
                            {status}
                        </span>
                    )}
                </div>
                <div className="mt-3">
                    <CandidateStageControls
                        key={current.id}
                        candidacy={current}
                        position={position}
                        access={access}
                        mode={mode}
                        disabled={locked}
                        siblings={siblings}
                        onChange={changed}
                        onReload={onReload}
                        onReloadAndFocus={onReloadAndFocus}
                    />
                </div>
            </div>
            {canOffer && OFFER_FROM.includes(current.stage) && (
                <div className="mb-3">
                    <CommitmentSummary candidacyId={current.id} mode={mode} />
                </div>
            )}
            <Tabs
                id={`${baseId}-tabs`}
                defaultActiveKey="application"
                variant="underline"
                className="mb-3 flex-nowrap"
            >
                <Tab eventKey="application" title="Application">
                    <CandidateKeyFacts
                        profile={current.profile}
                        mode={mode}
                        candidacyId={current.id}
                        headingLevel={level}
                    />
                    <Section level={level} title="Full application">
                        <CandidateAnswers
                            profile={current.profile}
                            program={current.program}
                            candidacyId={current.id}
                            mode={mode}
                            courseLabel={courseLabel}
                        />
                    </Section>
                    {coordinator && (
                        <CoordinatorDetails candidacy={current} level={level} />
                    )}
                    {coordinator && current.endReason && (
                        <Section level={level} title="Why it ended">
                            <EndReasonSummary endReason={current.endReason} />
                        </Section>
                    )}
                </Tab>
                {showInterview && (
                    <Tab eventKey="interview" title="Interview">
                        {interview && (
                            <p className="mb-3">
                                {interviewText(interview)}
                                {interview.details && (
                                    <span className="d-block">
                                        {interview.details}
                                    </span>
                                )}
                            </p>
                        )}
                        {canMoveStages && reviewable && (
                            <InterviewDetailsForm
                                candidacy={current}
                                disabled={locked}
                                onChange={changed}
                                onReload={onReload}
                            />
                        )}
                    </Tab>
                )}
                <Tab
                    eventKey="notes"
                    title={
                        feedbackCount > 0 ? `Notes (${feedbackCount})` : 'Notes'
                    }
                >
                    <CandidateFeedback
                        candidacy={current}
                        mode={mode}
                        disabled={locked}
                        onChange={changed}
                        onReload={onReload}
                    />
                </Tab>
                <Tab eventKey="history" title="History">
                    {offer && (
                        <Section level={level} title="Offer">
                            <p className="mb-1">
                                {`Offered ${formatDate(offer.offeredAt)}.`}
                                {offer.respondedAt &&
                                    ` Answered ${formatDate(offer.respondedAt)}.`}
                            </p>
                            {offer.message && (
                                <Paragraphs value={offer.message} />
                            )}
                        </Section>
                    )}
                    <Section level={level} title="Steps">
                        {(current.history || []).length > 0 ? (
                            <HistoryList
                                history={current.history}
                                mode={mode}
                            />
                        ) : (
                            <p className="mb-0">No steps recorded yet.</p>
                        )}
                    </Section>
                </Tab>
            </Tabs>
        </article>
    );
}
